import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { AsOf } from "../components/AsOf";
import { FixtureList } from "../components/FixtureList";
import { LeagueTable } from "../components/LeagueTable";

const when = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const FeedList = ({ items, prefix }) => (
  <div className="border border-zinc-800" data-testid={`${prefix}-items`}>
    {items.map((item, i) => (
      <a
        key={item.url}
        href={item.url}
        target="_blank"
        rel="noreferrer"
        data-testid={`${prefix}-item-${i}`}
        className="group flex flex-col gap-2 border-b border-zinc-900 bg-[#0b0b0b] p-5 transition-colors hover:bg-[#141414] md:flex-row md:items-center md:justify-between md:gap-8 md:p-6"
      >
        <span className="min-w-0">
          <span className="block text-sm leading-snug text-white transition-colors group-hover:text-ochre md:text-base">
            {item.title}
          </span>
          <span className="mono mt-2 block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
            {item.publisher}
            {item.from_federation ? <span className="ml-2 text-maasai">federation</span> : null}
          </span>
        </span>
        <span className="mono flex shrink-0 items-center gap-2 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
          {when(item.published_at)}
          <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-1" />
        </span>
      </a>
    ))}
  </div>
);

export default function FkfFeed() {
  const [d, setD] = useState(null);
  const [kpl, setKpl] = useState(null);
  const [table, setTable] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "The FKF Live Feed — Football Kenya Federation and Harambee Stars news",
    description:
      "Football Kenya Federation announcements and the Kenyan football press, refreshed every day. Headlines and publishers only, with every item linking back to the source.",
    image: cardUrl("league"),
  });

  useEffect(() => {
    api.get("/fkf-feed")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the feed. Refresh to try again."));
    api.get("/kpl-fixtures").then((r) => setKpl(r.data)).catch(() => setKpl(null));
    api.get("/kpl-table").then((r) => setTable(r.data)).catch(() => setTable(null));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="fkf-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="fkf-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="fkf-feed-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The national game</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre" data-testid="fkf-count">
              {(d.federation_items || []).length} from the federation ·{" "}
              {(d.items || []).length} from the Kenyan press
            </p>
            <a
              href={d.federation_url}
              target="_blank"
              rel="noreferrer"
              className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400 hover:text-ochre"
              data-testid="fkf-federation-link"
            >
              footballkenya.org <ExternalLink size={10} className="ml-0.5 inline align-baseline" />
            </a>
          </div>
        </Reveal>

        {(d.federation_items || []).length ? (
          <section className="mt-10" data-testid="fkf-federation-section">
            <h2 className="display-3 border-b border-white/15 pb-4">Straight From The Federation</h2>
            <div className="mt-6">
              <FeedList items={d.federation_items} prefix="fkf-federation" />
            </div>
          </section>
        ) : null}

        {kpl ? (
          <section className="mt-12" data-testid="kpl-fixtures-section">
            <h2 className="display-3 border-b border-white/15 pb-4">{kpl.title}</h2>
            <p className="body-copy mt-5 max-w-3xl">{kpl.standfirst}</p>
            <AsOf
              testId="kpl-source-stamp"
              kind={kpl.source.kind}
              asOf={kpl.read_at}
              publisher={`${kpl.source.publisher}${kpl.season ? ` · ${kpl.season} season` : ""}`}
              url={kpl.source.url}
            />
            <div className="mt-7 grid gap-8 lg:grid-cols-2">
              <div>
                <p className="overline">Next fixtures</p>
                <div className="mt-4">
                  <FixtureList matches={kpl.upcoming} prefix="kpl-upcoming" empty="No fixtures released yet." />
                </div>
              </div>
              <div>
                <p className="overline">Latest results</p>
                <div className="mt-4">
                  <FixtureList matches={kpl.results} prefix="kpl-results" empty="No results in the feed yet." />
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {table ? (
          <section className="mt-12" data-testid="kpl-table-section">
            <h2 className="display-3 border-b border-white/15 pb-4">{table.title}</h2>
            <p className="body-copy mt-5 max-w-3xl">{table.standfirst}</p>
            <AsOf
              testId="kpl-table-stamp"
              kind={table.source.kind}
              asOf={table.read_at}
              publisher={`${table.source.publisher}${table.season ? ` · ${table.season}` : ""}${
                table.started ? "" : " · season not started"
              }`}
              url={table.source.url}
            />
            <div className="mt-6">
              <LeagueTable table={table} />
            </div>
            {table.note ? (
              <p className="mt-4 max-w-3xl text-xs leading-relaxed text-zinc-500" data-testid="kpl-table-note">
                {table.note}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="mt-12">
          <h2 className="display-3 border-b border-white/15 pb-4">The Kenyan Football Press</h2>
          <div className="mt-6">
            <FeedList items={d.items} prefix="fkf" />
          </div>
        </section>

        <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="fkf-note">
          <p className="overline text-ochre">How this feed works</p>
          <p className="mt-4 max-w-4xl whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.note}</p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/national-game" className="btn-primary" data-testid="fkf-national-link">
            The national game plan <ArrowRight size={14} />
          </Link>
          <Link to="/fifa-ranking" className="btn-ghost" data-testid="fkf-ranking-link">
            Kenya in the FIFA ranking
          </Link>
          <Link to="/harambee-fixtures" className="btn-ghost" data-testid="fkf-fixtures-link">
            Harambee Stars fixtures
          </Link>
          <Link to="/news" className="btn-ghost" data-testid="fkf-news-link">
            Our own news and stories
          </Link>
        </div>
      </div>
    </div>
  );
}
